'use client';

import { useEffect, useRef } from 'react';

type UseScrollToCardParams = {
  cardId: string | null;
  hasData: unknown;
  isLoading: boolean;
};

export const useScrollToCard = ({ cardId, hasData, isLoading }: UseScrollToCardParams) => {
  const scrollToIdRef = useRef(cardId || null);
  const scrollDoneRef = useRef(false);

  if (cardId && !scrollDoneRef.current) scrollToIdRef.current = cardId;

  useEffect(() => {
    const id = scrollToIdRef.current;
    if (!id || !hasData || isLoading) return;

    scrollToIdRef.current = null;
    scrollDoneRef.current = true;

    const params = new URLSearchParams(window.location.search);
    params.delete('fromDetailId');
    params.delete('openedProperty');
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    // Use native replaceState to avoid triggering Next.js router, which would
    // cause useSearchParams() to suspend without a Suspense boundary → ErrorBoundary.
    (Object.getPrototypeOf(window.history) as typeof window.history).replaceState.call(
      window.history,
      null,
      '',
      newUrl,
    );

    let rafId: number;
    const tryScroll = () => {
      const el = document.getElementById(`card-${id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        rafId = requestAnimationFrame(tryScroll);
      }
    };
    rafId = requestAnimationFrame(tryScroll);

    return () => cancelAnimationFrame(rafId);
  }, [cardId, hasData, isLoading]);
};
